import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000SaveCancelDialog from '../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000AutoCompleteInput from '../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput';
import U2000ComboBox from '../../../u2000Core/components/formControls/combobox/U2000ComboBox';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import U2000CheckBox from '../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000InputCalendarCustom from '../../../u2000Core/components/formControls/input/U2000InputCalendarCustom';
import u2000FormState, { U2000FormStateEnumAction, U2000FormStateEnumEditMode, type U2000FormState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { U3129ProjectSearchPanel } from '../../../u3129Timesheet/components/form/U3129LineProjectSubprojectRowCells';
import { NS } from '../../U3147';
import { useU3147Context } from '../../U3147Context';
import { useU3147ManualSessionHandlers } from '../../U3147UseManualSessionHandlers';
import {
  useU3147FindProjectHandlers,
  useU3147FindSubprojectHandlers,
  useU3147FindTasksForProjectHandlers,
} from '../../U3147UseFindProjectSubprojectHandlers';
import {
  u3147FormatProjectLabel,
  u3147FormatTaskLabel,
  u3147IsAdmIncludeSubProjectEnabled,
  u3147IsSessionExported,
  u3147IsSessionRunning,
  type U3147FindProjectItem,
  type U3147FindSubprojectItem,
  type U3147TaskItem,
  type U3147TimeSession,
} from '../../U3147Service';
import {
  u3147BuildNewManualSessionDefaults,
  u3147CombineDateAndTimeToIso,
  u3147ComputeDurationMinutesFromStartEnd,
  u3147ComputeEndHmFromStartAndDuration,
  u3147ComputeSessionDurationMinutes,
  u3147FormatTimeFromIso,
  u3147HmToMinutes,
  u3147IsHmWithinWorkDay,
  u3147IsValidHm,
  u3147ResolveManualSessionEndIso,
  u3147WouldExceedWorkDayEnd,
  u3147ResolveGapBeforeNextSession,
  u3147ResolveOverlapFieldFeedback,
  u3147ResolveWorkDayEndFieldFeedback,
  type U3147ManualTimeField,
} from '../../tools/u3147FormatDuration';
import { u3147BuildOverlapFieldMessage } from '../../tools/u3147BuildOverlapFieldMessage';
import { u3147BuildWorkDayEndFieldMessage } from '../../tools/u3147BuildWorkDayEndFieldMessage';

const U3147_MANUAL_TIME_FIELDS: readonly U3147ManualTimeField[] = ['startTime', 'endTime', 'durationMinutes'];

export type U3147ManualFormRow = {
  workDate: string;
  startTime: string;
  endTime: string;
  useDurationMode: boolean;
  fillGapBeforeNext: boolean;
  durationMinutes: string;
  project: U3147FindProjectItem | null;
  subproject: U3147FindSubprojectItem | null;
  taskId: string;
  note: string;
};

function buildNewManualForm(
  defaultDate: string,
  existingSessions: U3147TimeSession[],
  roundingMinutes: number,
  organizationTimezone: string,
): U3147ManualFormRow {
  const { startTime, endTime, durationMinutes } = u3147BuildNewManualSessionDefaults(
    defaultDate,
    existingSessions,
    roundingMinutes,
    organizationTimezone,
  );

  return {
    workDate: defaultDate,
    startTime,
    endTime,
    useDurationMode: false,
    fillGapBeforeNext: false,
    durationMinutes,
    project: null,
    subproject: null,
    taskId: '',
    note: '',
  };
}

function sessionToForm(session: U3147TimeSession): U3147ManualFormRow {
  const duration = u3147ComputeSessionDurationMinutes(session.startedAt ?? undefined, session.endedAt ?? undefined, session.durationMinutes);
  return {
    workDate: session.workDate?.slice(0, 10) ?? '',
    startTime: u3147FormatTimeFromIso(session.startedAt),
    endTime: u3147FormatTimeFromIso(session.endedAt),
    useDurationMode: false,
    fillGapBeforeNext: false,
    durationMinutes: String(duration),
    project: (session.project as U3147FindProjectItem) ?? null,
    subproject: (session.subproject as U3147FindSubprojectItem) ?? null,
    taskId: session.taskId ?? session.task?.id ?? '',
    note: session.note ?? '',
  };
}

function syncManualFormDerivedFields(row: U3147ManualFormRow): U3147ManualFormRow {
  if (row.useDurationMode) {
    const nextEnd = u3147ComputeEndHmFromStartAndDuration(row.startTime, row.durationMinutes);
    if (nextEnd && nextEnd !== row.endTime) {
      return { ...row, endTime: nextEnd };
    }
    return row;
  }
  const nextDuration = u3147ComputeDurationMinutesFromStartEnd(row.startTime, row.endTime);
  if (nextDuration && nextDuration !== row.durationMinutes) {
    return { ...row, durationMinutes: nextDuration };
  }
  return row;
}

function projectNeedsSubproject(project: U3147FindProjectItem | null, admEnabled: boolean): boolean {
  if (!project || !admEnabled) return false;
  return project.includeSubProjet === true;
}

function u3147SameTaskList(a: U3147TaskItem[], b: U3147TaskItem[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((task, index) => task.id === b[index]?.id);
}

export interface U3147ManualSessionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  defaultDate: string;
  session?: U3147TimeSession | null;
  existingSessions?: U3147TimeSession[];
}

const U3147ManualSessionDialog: React.FC<U3147ManualSessionDialogProps> = ({
  isOpen,
  onClose,
  onSaved,
  defaultDate,
  session,
  existingSessions = [],
}) => {
  const { t, i18n } = useTranslation();
  const { initLU, roundingMinutes, organizationTimezone } = useU3147Context();
  const { busy, createSession, updateSession } = useU3147ManualSessionHandlers();
  const { search: searchProject } = useU3147FindProjectHandlers();
  const { fetchTasks } = useU3147FindTasksForProjectHandlers();
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;
  const existingSessionsRef = useRef(existingSessions);
  existingSessionsRef.current = existingSessions;
  const prevTimeFieldErrorsRef = useRef<Set<U3147ManualTimeField>>(new Set());
  const [tasks, setTasks] = useState<U3147TaskItem[]>([]);

  const admIncludeSubProjectEnabled = u3147IsAdmIncludeSubProjectEnabled(initLU.personalization);

  const [formState, formStateDispatch] = useReducer<U2000FormState, [action: { type: string; data?: unknown }]>(
    u2000FormState,
    u2000IntFormState,
  );

  const roundingMinutesRef = useRef(roundingMinutes);
  roundingMinutesRef.current = roundingMinutes;
  const organizationTimezoneRef = useRef(organizationTimezone);
  organizationTimezoneRef.current = organizationTimezone;

  const formStateHandlers = useU2000FormStateHandlers<U3147ManualFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () =>
        buildNewManualForm(
          defaultDate,
          existingSessionsRef.current,
          roundingMinutesRef.current,
          organizationTimezoneRef.current,
        ),
    },
  });

  const data = formStateHandlers.getData();
  const project = data?.project ?? null;
  const subproject = data?.subproject ?? null;
  const useDurationMode = !!data?.useDurationMode;

  const projectIdRef = useRef<string | null>(null);
  projectIdRef.current = project?.id?.trim() ?? null;
  const getProjectId = useCallback(() => projectIdRef.current, []);
  const findSubprojectHandlers = useU3147FindSubprojectHandlers({ getProjectId });

  useEffect(() => {
    if (!isOpen) {
      prevTimeFieldErrorsRef.current = new Set();
      return;
    }
    const initial = session
      ? sessionToForm(session)
      : buildNewManualForm(
          defaultDate,
          existingSessionsRef.current,
          roundingMinutesRef.current,
          organizationTimezoneRef.current,
        );
    formStateHandlers.setEditMode(session ? U2000FormStateEnumEditMode.EDIT : U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setData(initial);
    formStateHandlers.setErrors({});
  }, [isOpen, session, defaultDate]);

  useEffect(() => {
    if (!isOpen) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    const synced = syncManualFormDerivedFields(row);
    if (synced !== row) {
      formStateHandlers.setData(synced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recalcul Fin ↔ Durée selon le mode actif
  }, [isOpen, data?.startTime, data?.endTime, data?.durationMinutes, data?.useDurationMode]);

  useEffect(() => {
    const pid = project?.id?.trim();
    if (!pid) {
      setTasks((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let cancelled = false;
    void fetchTasksRef.current({
      projectId: pid,
      subprojectId: subproject?.id?.trim() || null,
    }).then((list) => {
      if (cancelled) return;
      setTasks((prev) => (u3147SameTaskList(prev, list) ? prev : list));
    });
    return () => {
      cancelled = true;
    };
  }, [project?.id, subproject?.id]);

  const taskOptions = useMemo(() => tasks, [tasks]);

  const onSearchProject = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => searchProject(criterias),
    [searchProject],
  );

  const onSearchSubproject = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => findSubprojectHandlers.search(criterias),
    [findSubprojectHandlers],
  );

  const extractProject = useCallback((item: U3147FindProjectItem | null) => {
    if (item) {
      return [
        { fieldName: 'project', value: item },
        { fieldName: 'subproject', value: null },
        { fieldName: 'taskId', value: '' },
      ];
    }
    return [
      { fieldName: 'project', value: null },
      { fieldName: 'subproject', value: null },
      { fieldName: 'taskId', value: '' },
    ];
  }, []);

  const extractSubproject = useCallback((item: U3147FindSubprojectItem | null) => {
    if (item) {
      return [
        { fieldName: 'subproject', value: item },
        { fieldName: 'taskId', value: '' },
      ];
    }
    return [{ fieldName: 'subproject', value: null }, { fieldName: 'taskId', value: '' }];
  }, []);

  const checkOverlap = useCallback(
    (startIso: string, endIso: string) => {
      const start = new Date(startIso).getTime();
      const end = new Date(endIso).getTime();
      if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return false;
      for (const s of existingSessions) {
        if (session?.id && s.id === session.id) continue;
        if (!s.startedAt || !s.endedAt) continue;
        const sStart = new Date(s.startedAt).getTime();
        const sEnd = new Date(s.endedAt).getTime();
        if (Number.isNaN(sStart) || Number.isNaN(sEnd)) continue;
        if (start < sEnd && end > sStart) return true;
      }
      return false;
    },
    [existingSessions, session?.id],
  );

  const resolvedSessionTimes = useMemo(() => {
    if (!data) return null;
    if (!u3147IsValidHm(data.startTime)) return null;
    const endedAt = u3147ResolveManualSessionEndIso(
      data.workDate,
      data.startTime,
      data.endTime,
      !!data.useDurationMode,
      data.durationMinutes,
    );
    if (!endedAt) return null;
    const startedAt = u3147CombineDateAndTimeToIso(data.workDate, data.startTime, organizationTimezone);
    return { startedAt, endedAt };
  }, [data, organizationTimezone]);

  const hasOverlapConflict = useMemo(() => {
    if (!resolvedSessionTimes) return false;
    return checkOverlap(resolvedSessionTimes.startedAt, resolvedSessionTimes.endedAt);
  }, [resolvedSessionTimes, checkOverlap]);

  const gapBeforeNext = useMemo(() => {
    if (!data?.workDate?.trim() || !u3147IsValidHm(data.startTime)) return null;
    return u3147ResolveGapBeforeNextSession(data.workDate, data.startTime, existingSessions, session?.id);
  }, [data?.workDate, data?.startTime, existingSessions, session?.id]);

  useEffect(() => {
    if (!isOpen || !data) return;
    if (data.fillGapBeforeNext && !gapBeforeNext) {
      formStateHandlers.setData({ ...data, fillGapBeforeNext: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- décocher si le créneau n'est plus disponible
  }, [isOpen, gapBeforeNext, data?.fillGapBeforeNext]);

  useEffect(() => {
    if (!isOpen || !data?.fillGapBeforeNext || !gapBeforeNext) return;
    const row = { ...data };
    let changed = false;
    if (!row.useDurationMode) {
      row.useDurationMode = true;
      changed = true;
    }
    const nextDuration = String(gapBeforeNext.gapDurationMinutes);
    if (row.durationMinutes !== nextDuration) {
      row.durationMinutes = nextDuration;
      changed = true;
    }
    if (changed) {
      formStateHandlers.setData(syncManualFormDerivedFields(row));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remplir le créneau jusqu'à la prochaine plage
  }, [
    isOpen,
    data?.fillGapBeforeNext,
    data?.startTime,
    data?.workDate,
    gapBeforeNext?.gapDurationMinutes,
    gapBeforeNext?.nextStartHm,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const errors = { ...formStateHandlers.getErrors() };
    let errorsChanged = false;

    const workDayEndFields =
      data && u3147IsValidHm(data.startTime)
        ? u3147ResolveWorkDayEndFieldFeedback(
            data.startTime,
            data.endTime,
            data.durationMinutes,
            !!data.useDurationMode,
          )
        : [];
    const workDayEndFieldSet = new Set(workDayEndFields);

    const overlapFeedback =
      hasOverlapConflict && resolvedSessionTimes && data
        ? u3147ResolveOverlapFieldFeedback(resolvedSessionTimes.startedAt, resolvedSessionTimes.endedAt, existingSessions, {
            excludeSessionId: session?.id,
            useDurationMode: !!data.useDurationMode,
          })
        : [];
    const feedbackByField = new Map(overlapFeedback.map((item) => [item.field, item.hint]));
    const activeFieldSet = new Set<U3147ManualTimeField>();

    for (const field of U3147_MANUAL_TIME_FIELDS) {
      if (workDayEndFieldSet.has(field)) {
        const message = u3147BuildWorkDayEndFieldMessage(
          field,
          {
            useDurationMode: !!data?.useDurationMode,
            startTime: data?.startTime ?? '',
            endTime: data?.endTime ?? '',
          },
          t,
        );
        activeFieldSet.add(field);
        if (errors[field] !== message) {
          errors[field] = message;
          errorsChanged = true;
        }
        formStateDispatch({ type: U2000FormStateEnumAction.SETTOUCHED, data: { id: field } });
        continue;
      }
      const hint = feedbackByField.get(field);
      if (hint) {
        const message = u3147BuildOverlapFieldMessage(field, hint, t);
        activeFieldSet.add(field);
        if (errors[field] !== message) {
          errors[field] = message;
          errorsChanged = true;
        }
        formStateDispatch({ type: U2000FormStateEnumAction.SETTOUCHED, data: { id: field } });
      }
    }

    for (const field of prevTimeFieldErrorsRef.current) {
      if (!activeFieldSet.has(field) && errors[field]) {
        delete errors[field];
        errorsChanged = true;
      }
    }

    prevTimeFieldErrorsRef.current = activeFieldSet;

    if (errorsChanged || activeFieldSet.size > 0) {
      formStateHandlers.setErrors(errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- messages de validation horaires par champ
  }, [
    hasOverlapConflict,
    isOpen,
    t,
    data?.useDurationMode,
    data?.startTime,
    data?.endTime,
    data?.durationMinutes,
    resolvedSessionTimes,
    existingSessions,
    session?.id,
  ]);

  const isValid = useMemo(() => {
    if (!data) return false;
    if (!data.workDate?.trim() || !data.project?.id?.trim()) return false;
    if (!u3147IsValidHm(data.startTime)) return false;
    if (data.useDurationMode) {
      const mins = Number(data.durationMinutes.trim());
      if (!Number.isFinite(mins) || mins <= 0 || !Number.isInteger(mins)) return false;
      if (u3147WouldExceedWorkDayEnd(data.startTime, mins)) return false;
    } else if (
      !u3147IsValidHm(data.endTime) ||
      !u3147IsHmWithinWorkDay(data.endTime) ||
      u3147HmToMinutes(data.endTime) <= u3147HmToMinutes(data.startTime)
    ) {
      return false;
    }
    if (projectNeedsSubproject(data.project, admIncludeSubProjectEnabled) && !data.subproject?.id?.trim()) return false;
    if (!resolvedSessionTimes) return false;
    if (hasOverlapConflict) return false;
    return true;
  }, [data, admIncludeSubProjectEnabled, resolvedSessionTimes, hasOverlapConflict]);

  const handleSave = async () => {
    if (!data?.project?.id || !isValid || !resolvedSessionTimes || hasOverlapConflict) return;
    const { startedAt, endedAt } = resolvedSessionTimes;

    const payload = {
      projectId: data.project.id,
      subprojectId: data.subproject?.id?.trim() || null,
      taskId: data.taskId?.trim() || null,
      workDate: data.workDate.slice(0, 10),
      startedAt,
      endedAt,
      note: data.note?.trim() || null,
    };

    const result = session?.id
      ? await updateSession(session.id, { id: session.id, ...payload, version: session.version })
      : await createSession(payload);

    if (result) {
      onSaved();
      onClose();
    }
  };

  const readonly = session ? u3147IsSessionExported(session) || u3147IsSessionRunning(session) : false;

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={(action) => {
        if (action === 'save') void handleSave();
        else onClose();
      }}
      title={session ? t('lblEditRange', { ns: NS, defaultValue: 'Modifier la plage' }) : t('lblAddRange', { ns: NS, defaultValue: 'Ajouter une plage' })}
      isValid={isValid && !busy && !readonly}
      readOnly={readonly}
      width="large"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <U2000InputCalendarCustom
          fieldName="workDate"
          label={t('lblDate', { ns: NS, defaultValue: 'Date' })}
          formStateHandlers={formStateHandlers}
          readonly={readonly}
          required
        />
        <U2000InputText
          fieldName="startTime"
          label={t('colStart', { ns: NS, defaultValue: 'Début' })}
          formStateHandlers={formStateHandlers}
          placeholder="HH:MM"
          readonly={readonly}
          required
        />
        <U2000CheckBox
          fieldName="useDurationMode"
          label={t('lblUseDurationMode', { ns: NS, defaultValue: 'Saisir une durée en minutes' })}
          formStateHandlers={formStateHandlers}
          readonly={readonly}
        />
        {gapBeforeNext ? (
          <U2000CheckBox
            fieldName="fillGapBeforeNext"
            label={t('lblFillGapBeforeNext', {
              ns: NS,
              time: gapBeforeNext.nextStartHm,
              defaultValue: `Occuper le créneau jusqu'à ${gapBeforeNext.nextStartHm}`,
            })}
            formStateHandlers={formStateHandlers}
            readonly={readonly}
          />
        ) : (
          <div className="hidden sm:block" aria-hidden="true" />
        )}
        <U2000InputText
          fieldName="endTime"
          label={t('colEnd', { ns: NS, defaultValue: 'Fin' })}
          formStateHandlers={formStateHandlers}
          placeholder="HH:MM"
          readonly={readonly || useDurationMode}
          required={!useDurationMode}
        />
        <U2000InputText
          fieldName="durationMinutes"
          label={t('lblDurationMinutes', { ns: NS, defaultValue: 'Durée (minutes)' })}
          formStateHandlers={formStateHandlers}
          placeholder="60"
          readonly={readonly || !useDurationMode}
          required={useDurationMode}
        />
        <div className="sm:col-span-2">
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers as never}
            fieldName="project"
            mainFieldName="projetNumber"
            entityLink="U3121"
            label={t('colProject', { ns: NS, defaultValue: 'Projet' })}
            onSearch={onSearchProject}
            displayFn={(item) => u3147FormatProjectLabel(item as U3147FindProjectItem)}
            overrideExtractDataFn={(item) => extractProject(item as U3147FindProjectItem | null)}
            resultLabels={[]}
            resultFields={['projetNumber', 'projetName', 'customerName']}
            dropdownWidth={520}
            renderPanel={(props) => <U3129ProjectSearchPanel {...props} ns="u3129" />}
            readonly={readonly}
            required
          />
        </div>
        {admIncludeSubProjectEnabled && projectNeedsSubproject(project, true) && (
          <div className="sm:col-span-2">
            <U2000AutoCompleteInput
              formStateHandlers={formStateHandlers as never}
              fieldName="subproject"
              mainFieldName="subProjetNumber"
              label={t('colSubproject', { ns: NS, defaultValue: 'Sous-projet' })}
              onSearch={onSearchSubproject}
              displayFn={(item) => {
                const sp = item as U3147FindSubprojectItem;
                return sp?.subProjetNumber ?? sp?.id ?? '';
              }}
              overrideExtractDataFn={(item) => extractSubproject(item as U3147FindSubprojectItem | null)}
              resultLabels={[]}
              resultFields={['subProjetNumber', 'subProjetName']}
              readonly={readonly || !project?.id}
              required
            />
          </div>
        )}
        <div className="sm:col-span-2">
          <U2000ComboBox
            fieldName="taskId"
            label={t('colTask', { ns: NS, defaultValue: 'Tâche' })}
            data={taskOptions}
            keyField="id"
            formatLabel={(item) => u3147FormatTaskLabel(item as U3147TaskItem, i18n.language)}
            formStateHandlers={formStateHandlers}
            readonly={readonly || !project?.id}
          />
        </div>
        <div className="sm:col-span-2">
          <U2000InputText
            fieldName="note"
            label={t('colNote', { ns: NS, defaultValue: 'Note' })}
            formStateHandlers={formStateHandlers}
            readonly={readonly}
          />
        </div>
        {session && u3147IsSessionExported(session) && (
          <p className="sm:col-span-2 text-sm text-amber-700">
            {t('msgExportedEditWarning', {
              ns: NS,
              defaultValue: 'Cette plage a déjà été exportée — modifier nécessitera un nouvel export.',
            })}
          </p>
        )}
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3147ManualSessionDialog;
